"use client";

import React from "react";
import {
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  ExternalLink,
  FileSignature,
  FileText,
  Mail,
  Send,
  User,
} from "lucide-react";

import type { Contracts } from "@/src/gql/graphql";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CompanyDetailsTab } from "@/components/shared/detail-tabs/CompanyDetailsTab";
import { AISummary } from "@/components/ai/ai-summary";
import { formatHeaderDate } from "@/components/shared/UniversalDetailPage/header/model";

// Status → badge styling. Mirrors the lifecycle in ContractDetailConfig.
const STATUS_META: Record<string, { label: string; className: string }> = {
  draft: { label: "Draft", className: "bg-gray-100 text-gray-700" },
  sent: { label: "Sent", className: "bg-blue-100 text-blue-700" },
  viewed: { label: "Viewed", className: "bg-indigo-100 text-indigo-700" },
  partially_signed: { label: "Partially Signed", className: "bg-amber-100 text-amber-700" },
  signed: { label: "Signed", className: "bg-green-100 text-green-700" },
  declined: { label: "Declined", className: "bg-red-100 text-red-700" },
  withdrawn: { label: "Withdrawn", className: "bg-red-100 text-red-700" },
  error: { label: "Error", className: "bg-red-100 text-red-700" },
};

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? { label: status, className: "bg-gray-100 text-gray-700" };
  return <Badge className={meta.className}>{meta.label}</Badge>;
}

function Fact({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: React.ReactNode;
}) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <div className="text-sm text-card-foreground break-words">{value}</div>
      </div>
    </div>
  );
}

/**
 * Sales-oriented contract overview — the default landing tab. Surfaces the
 * document/template, status, the parties it's with, the deal it's tied to, the
 * sent/signed timeline, quick links (signing link / signed PDF), plus an AI
 * summary of the related company's recent activity for sales context.
 */
export function ContractOverviewTab({ item: contract }: { item: Contracts }) {
  const companyId =
    typeof contract.company_id === "string" && contract.company_id.length > 0
      ? contract.company_id
      : null;
  const recipient =
    contract.recipient_name || contract.recipient_email || contract.contacts?.name || null;

  return (
    <div className="min-h-[400px] space-y-6">
      {/* Header summary card */}
      <div className="rounded-lg border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <FileSignature className="mt-0.5 h-6 w-6 text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-card-foreground">
                {contract.document_name || contract.template_name || "Contract"}
              </h2>
              {contract.template_name && contract.document_name && (
                <p className="text-sm text-muted-foreground">Template: {contract.template_name}</p>
              )}
            </div>
          </div>
          <StatusBadge status={contract.status} />
        </div>

        {/* Quick links. The document comes first — it's what a rep is here for.
            It opens our own Document tab, NOT `signed_pdf_url`: that's an
            eSignatures link which expires ~3 days after signing, so it was a
            dead button on every contract older than that. */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button asChild variant="default" size="sm">
            <a href={`/contracts/${contract.id}?tab=document`}>
              <FileText className="h-4 w-4" />
              View document
            </a>
          </Button>
          {contract.signing_url && contract.status !== "signed" && (
            <Button asChild variant="outline" size="sm">
              <a href={contract.signing_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" />
                Open signing link
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Who it's with */}
      <div className="rounded-lg border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold text-card-foreground">Who it&apos;s with</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Fact icon={Building2} label="Company" value={contract.companies?.name} />
          <Fact icon={User} label="Contact" value={contract.contacts?.name} />
          <Fact icon={Mail} label="Recipient" value={recipient} />
          {contract.recipient_email && contract.recipient_email !== recipient && (
            <Fact icon={Mail} label="Recipient email" value={contract.recipient_email} />
          )}
          <Fact
            icon={Briefcase}
            label="Deal"
            value={
              contract.deals?.title ??
              (typeof contract.deal_id === "string" && contract.deal_id.length > 0
                ? "Linked deal"
                : "Not tied to a deal")
            }
          />
          <Fact
            icon={Send}
            label="Delivery"
            value={contract.delivery_mode ? contract.delivery_mode : null}
          />
        </div>
      </div>

      {/* Timeline */}
      <div className="rounded-lg border bg-card p-5">
        <h3 className="mb-4 text-sm font-semibold text-card-foreground">Timeline</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Fact icon={Send} label="Sent" value={formatHeaderDate(contract.sent_at)} />
          <Fact icon={CheckCircle2} label="Signed" value={formatHeaderDate(contract.signed_at)} />
          <Fact icon={Calendar} label="Last event" value={formatHeaderDate(contract.last_event_at)} />
        </div>
        {contract.decline_reason && (
          <p className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">
            Decline reason: {contract.decline_reason}
          </p>
        )}
      </div>

      {/* Company context — reuse company details + AI activity summary */}
      {companyId && (
        <>
          <CompanyDetailsTab companyId={companyId} embedded />
          <AISummary
            entityType="company"
            entityId={companyId}
            summaryType="activity"
            showMetadata
            showRefreshButton
            className="mb-0"
          />
        </>
      )}
    </div>
  );
}
